import React, { useRef } from 'react';
import { clsx } from 'clsx';
import { Icon } from '@/shared/icons';

/**
 * Props do ToolbarSearch
 */
export interface ToolbarSearchProps {
  value: string;
  onChange: (value: string) => void;
  /** Placeholder curto, reticências tipográficas "…" (nunca "...") */
  placeholder?: string;
  /** Classes de encolhimento progressivo aplicadas no wrapper — ex.: "ds-search hidden sm:flex" (regras @container em index.css) */
  shrinkClass?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Campo de busca canônico de toolbar (F17-F22): 38px, rounded-[9px], ícone
 * search 16px, fonte 14, max-w 360px, botão X de limpar e anel de foco.
 *
 * Espelha os tokens/anel do Input shared, mas a borda vive no WRAPPER (não no
 * <input>) — obrigatório para o colapso @container (`.ds-search input
 * { display: none }` a 880px precisa manter o quadrado 44px com borda + ícone).
 *
 * @example
 * ```tsx
 * <ToolbarSearch
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   placeholder="Buscar por nome ou e-mail…"
 *   shrinkClass="ds-search hidden sm:flex"
 *   data-testid="users-search"
 * />
 * ```
 */
export const ToolbarSearch: React.FC<ToolbarSearchProps> = ({
  value,
  onChange,
  placeholder = 'Buscar…',
  shrinkClass,
  disabled = false,
  'data-testid': dataTestId = 'toolbar-search',
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasValue = value !== '';

  // Anel de foco herdado do Input shared (focus-within manual — a borda é do wrapper)
  const handleFocus = () => {
    const el = wrapperRef.current;
    if (!el || disabled) return;
    el.style.borderColor = 'var(--theme-input-focus)';
    el.style.boxShadow = '0 0 0 3px var(--ring-color)';
  };

  const handleBlur = () => {
    const el = wrapperRef.current;
    if (!el) return;
    el.style.borderColor = 'var(--theme-input-border)';
    el.style.boxShadow = 'none';
  };

  return (
    <div
      ref={wrapperRef}
      className={clsx(
        'flex items-center gap-2 h-[38px] px-3 rounded-[9px] border w-full max-w-[360px]',
        disabled && 'opacity-50',
        shrinkClass
      )}
      style={{
        backgroundColor: 'var(--theme-input-background)',
        borderColor: 'var(--theme-input-border)',
        transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
      }}
    >
      <Icon name="search" className="w-4 h-4 flex-none" style={{ color: 'var(--theme-muted-text)' }} />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:opacity-60 disabled:cursor-not-allowed"
        style={{ color: 'var(--theme-input-text)' }}
        data-testid={dataTestId}
      />
      {hasValue && (
        <button
          type="button"
          aria-label="Limpar busca"
          onClick={() => {
            onChange('');
            inputRef.current?.focus();
          }}
          className="flex-none inline-flex items-center justify-center w-5 h-5 rounded opacity-60 hover:opacity-100 transition-opacity"
          style={{ color: 'var(--theme-muted-text)' }}
          data-testid={`${dataTestId}-clear`}
        >
          <Icon name="x" className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
